const AUTH_SESSION_KEY = "gout-food-scanner.auth0.session";
const AUTH_TRANSACTION_KEY = "gout-food-scanner.auth0.transaction";
const EXPIRY_SKEW_MS = 60_000;

export interface Auth0Config {
	domain: string;
	clientId: string;
	audience: string;
}

export interface AuthSession {
	accessToken: string;
	expiresAt: number;
	user: {
		id: string;
		name?: string;
		email?: string;
	};
}

interface AuthTransaction {
	state: string;
	codeVerifier: string;
}

interface TokenResponse {
	access_token?: unknown;
	expires_in?: unknown;
	id_token?: unknown;
}

function base64Url(bytes: Uint8Array): string {
	let value = "";
	for (const byte of bytes) value += String.fromCharCode(byte);
	return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomValue(byteLength: number): string {
	return base64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

async function codeChallenge(verifier: string): Promise<string> {
	const digest = await crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(verifier),
	);
	return base64Url(new Uint8Array(digest));
}

function normalizeDomain(value: string): string {
	const candidate = value.includes("://") ? value : `https://${value}`;
	const url = new URL(candidate);
	if (
		url.protocol !== "https:" ||
		url.pathname !== "/" ||
		url.search ||
		url.hash
	) {
		throw new Error("VITE_AUTH0_DOMAIN phải là tên miền Auth0 dùng HTTPS.");
	}
	return url.origin;
}

function decodeUserClaims(idToken: string): AuthSession["user"] | null {
	try {
		const payload = idToken.split(".")[1];
		if (!payload) return null;
		const encoded = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=");
		const claims = JSON.parse(atob(padded)) as Record<string, unknown>;
		if (typeof claims.sub !== "string") return null;
		return {
			id: claims.sub,
			name: typeof claims.name === "string" ? claims.name : undefined,
			email: typeof claims.email === "string" ? claims.email : undefined,
		};
	} catch {
		return null;
	}
}

export class Auth0Service {
	private readonly origin: string;

	constructor(private readonly config: Auth0Config) {
		this.origin = normalizeDomain(config.domain);
	}

	getSession(): AuthSession | null {
		const value = sessionStorage.getItem(AUTH_SESSION_KEY);
		if (!value) return null;

		try {
			const session = JSON.parse(value) as AuthSession;
			if (
				typeof session.accessToken !== "string" ||
				typeof session.expiresAt !== "number" ||
				typeof session.user !== "object" ||
				session.user === null ||
				typeof session.user.id !== "string" ||
				session.expiresAt <= Date.now() + EXPIRY_SKEW_MS
			) {
				this.clearSession();
				return null;
			}
			return session;
		} catch {
			this.clearSession();
			return null;
		}
	}

	async login(): Promise<void> {
		const transaction: AuthTransaction = {
			state: randomValue(32),
			codeVerifier: randomValue(64),
		};
		sessionStorage.setItem(AUTH_TRANSACTION_KEY, JSON.stringify(transaction));

		const parameters = new URLSearchParams({
			response_type: "code",
			client_id: this.config.clientId,
			redirect_uri: window.location.origin,
			scope: "openid profile email",
			audience: this.config.audience,
			state: transaction.state,
			code_challenge: await codeChallenge(transaction.codeVerifier),
			code_challenge_method: "S256",
		});
		window.location.assign(`${this.origin}/authorize?${parameters.toString()}`);
	}

	async handleRedirectCallback(): Promise<AuthSession | null> {
		const parameters = new URLSearchParams(window.location.search);
		const code = parameters.get("code");
		const state = parameters.get("state");
		const authError = parameters.get("error");
		if (!code && !state && !authError) return this.getSession();

		this.clearCallbackUrl();
		if (authError) {
			throw new Error(
				parameters.get("error_description") ||
					"Auth0 không thể hoàn tất đăng nhập.",
			);
		}

		const stored = sessionStorage.getItem(AUTH_TRANSACTION_KEY);
		sessionStorage.removeItem(AUTH_TRANSACTION_KEY);
		if (!code || !state || !stored) {
			throw new Error("Phiên đăng nhập Auth0 không hợp lệ. Vui lòng thử lại.");
		}

		const transaction = JSON.parse(stored) as AuthTransaction;
		if (state !== transaction.state || !transaction.codeVerifier) {
			throw new Error("Không thể xác minh yêu cầu đăng nhập Auth0.");
		}

		const response = await fetch(`${this.origin}/oauth/token`, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams({
				grant_type: "authorization_code",
				client_id: this.config.clientId,
				code,
				code_verifier: transaction.codeVerifier,
				redirect_uri: window.location.origin,
			}),
		});
		const tokens = (await response.json()) as TokenResponse;
		if (
			!response.ok ||
			typeof tokens.access_token !== "string" ||
			typeof tokens.expires_in !== "number"
		) {
			throw new Error("Không thể đổi mã đăng nhập lấy access token.");
		}

		const user =
			typeof tokens.id_token === "string"
				? decodeUserClaims(tokens.id_token)
				: null;
		if (!user) {
			throw new Error("Auth0 không trả về mã định danh người dùng ổn định.");
		}

		const session: AuthSession = {
			accessToken: tokens.access_token,
			expiresAt: Date.now() + tokens.expires_in * 1_000,
			user,
		};
		sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
		return session;
	}

	logout(): void {
		this.clearSession();
		const parameters = new URLSearchParams({
			client_id: this.config.clientId,
			returnTo: window.location.origin,
		});
		window.location.assign(`${this.origin}/v2/logout?${parameters.toString()}`);
	}

	clearSession(): void {
		sessionStorage.removeItem(AUTH_SESSION_KEY);
	}

	private clearCallbackUrl(): void {
		window.history.replaceState({}, document.title, window.location.pathname);
	}
}
